import { Button, Css, ModalBody, ModalFooter, ModalHeader, SelectCardGroup, useModal } from "@homebound/beam";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchBox } from "src/components";
import { MaterialType, Order } from "src/generated/graphqlTypes";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlansDetailImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { SwapProductFormSwap } from "../swapProductWorkflow";
import { replacementTag } from "../utils";
import {
  ChooseReplacementPossibleMaterials_MaterialVariantFragment,
  ChooseReplacementProjectItemDetails_ProjectItemFragment,
  ChooseReplacementProjectItemDetailsQuery,
  useChooseReplacementPossibleMaterialsQuery,
  useChooseReplacementProjectItemDetailsQuery,
} from "./ChooseReplacementModal.generated";

type ChooseReplacementModalProps = {
  /** The original PI being replaced; its placeholder requirements drive the search. */
  projectItemId: string;
  onSelect: (replacement: SwapProductFormSwap) => void;
  initialSelectedMaterialVariantId?: string;
};

export function ChooseReplacementModal(props: ChooseReplacementModalProps) {
  const { projectItemId, onSelect, initialSelectedMaterialVariantId } = props;
  const query = useChooseReplacementProjectItemDetailsQuery({ variables: { projectItemId } });
  return queryResult(query, ({ projectItem }) => (
    <ChooseReplacementModalView
      onSelect={onSelect}
      projectItem={projectItem}
      initialSelectedMaterialVariantId={initialSelectedMaterialVariantId}
    />
  ));
}

type ChooseReplacementModalViewProps = Pick<
  ChooseReplacementModalProps,
  "onSelect" | "initialSelectedMaterialVariantId"
> & { projectItem: ChooseReplacementProjectItemDetailsQuery["projectItem"] };

function ChooseReplacementModalView(props: ChooseReplacementModalViewProps) {
  const { projectItem, onSelect, initialSelectedMaterialVariantId = null } = props;
  const { closeModal } = useModal();
  const [search, setSearch] = useState("");
  const [selectedMaterialVariantId, setSelectedMaterialVariantId] = useState<string | null>(
    initialSelectedMaterialVariantId,
  );

  const query = useChooseReplacementPossibleMaterialsQuery({
    variables: {
      projectItemId: projectItem.id,
      filter: { type: [MaterialType.Product], itemId: [projectItem.item.id], search },
      orderBy: { lastUsedAt: Order.Desc },
    },
  });
  return (
    <>
      <ModalHeader>Choose the Replacement Product</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.$}>
            <SearchBox onSearch={setSearch} updateQueryString={false} />
          </div>
          {queryResult(query, ({ materialVariants }) => (
            <ReplacementList
              projectItem={projectItem}
              candidates={materialVariants.entities.filter((mv) => mv.id !== projectItem.materialVariant?.id)}
              selectedMaterialVariantId={selectedMaterialVariantId}
              onChange={setSelectedMaterialVariantId}
            />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Select"
          disabled={!selectedMaterialVariantId}
          onClick={() => {
            // Flag it as coming from the catalog, so the replacement card re-opens this modal
            // rather than the "define a product" form.
            onSelect({
              proposedMaterialVariantId: selectedMaterialVariantId!,
              proposedMaterialVariantWasCreated: false,
            });
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type ReplacementListProps = {
  projectItem: ChooseReplacementProjectItemDetails_ProjectItemFragment;
  candidates: ChooseReplacementPossibleMaterials_MaterialVariantFragment[];
  selectedMaterialVariantId: string | null;
  onChange: (materialVariantId: string | null) => void;
};

function ReplacementList(props: ReplacementListProps) {
  const { projectItem, candidates, selectedMaterialVariantId, onChange } = props;
  const isPlanBasedSwap = projectItem.bidContractLineItem?.forCoveringTakeoffs;
  const bidPackage = projectItem.bidContractLineItem?.revision?.bidPackageVersion?.bidPackage;
  const bidPackageLink = bidPackage?.bidPackageGroup && (
    <Link to={createBidPackagesDetailUrl(bidPackage.bidPackageGroup.id, bidPackage.id)} target="_blank">
      {bidPackage.name}
    </Link>
  );
  const options = useMemo(
    () =>
      candidates
        // If this is a plan-based swap, only show products that have an awarded cost
        .filter((mv) => (isPlanBasedSwap ? mv.hasAwardedCost : true))
        .map((mv) => ({
          value: mv.id,
          label: mv.listing.brand?.name ?? mv.listing.name ?? "",
          description: mv.displayName,
          image: mv.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
          ...replacementTag(mv),
        })),
    [candidates, isPlanBasedSwap],
  );

  if (options.isEmpty) {
    return (
      <EmptyState
        svg={<EmptyPlansDetailImage />}
        message={
          <div css={Css.df.fdc.gap1.tac.$}>
            <span>{isPlanBasedSwap ? `No awarded products for the selected product` : "No matching products"}</span>
            <span css={Css.sm.gray700.mw("420px").$}>
              {isPlanBasedSwap ? (
                <>
                  Only products with an awarded cost on {bidPackageLink ?? "this item's bid package"} can be swapped in,
                  and none are available for this item yet.
                </>
              ) : (
                "Try a different search to find a replacement product."
              )}
            </span>
          </div>
        }
      />
    );
  }

  return (
    <SelectCardGroup
      label="Replacement Products"
      options={options}
      value={selectedMaterialVariantId}
      onChange={onChange}
      layout="horizontal"
      view="grid"
    />
  );
}
